// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import { FeatureCollection } from 'geojson';

import Select2, { DataSelect2 } from 'wdna-select2';

import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { setFilterData } from 'wdna-components/lib/mainfilter/actions';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { sensor, SensorDO } from '../../../../sensors/requests';

interface Props {
  rowId?: number;
}

interface State {
  id?: number;
  sensorArray: SensorDO[];
}

export default class Filter extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private firstRender: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.firstRender = true;

    this.state = {
      sensorArray: []
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public componentDidUpdate(): void {
    const { firstRender } = this;
    if (firstRender) {
      this.firstRender = false;
      this.exportInitialData();
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    const filter: Record<string, any> = {};
    filter.status = 'ON_AIR';

    const idSensor: number | undefined = this.props.rowId;
    if (idSensor) filter.id = idSensor;

    this.loadingStart();
    sensor<SensorDO[]>('list', undefined, filter)
      ?.then((resp: SensorDO[]) => {
        if (resp && resp.length > 0) {
          this.setLocalState({
            sensorArray: resp,
            id: idSensor,
          });
        } else {
          messageWarning('Warning', 'There are not active sensors in the system');
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public submit = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const req: Record<string, any> = {};
    if (this.state.id && this.state.id > 0) {
      req.id = this.state.id;
    }

    if (!this.chkFilter(req)) return;

    this.loadingStart();
    sensor<FeatureCollection>('gis_position', undefined, req)
      ?.then((resp: FeatureCollection) => {
        this.readingsLoad(resp);
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public readingsLoad = (mapData: FeatureCollection): void => {
    this.context?.updateParent({
      update: window.getStr(),
      loading: true,
      mapData,
    });
  };

  public chkFilter = (_data: Record<string, any>): boolean => {
    return true;
  };

  public loadingStart = (): void => {
    this.context?.updateParent({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public exportInitialData = (): void => {
    this.setLocalState({}, () => {
      this.notifyFilterChange();
      this.exportMainFilter();
    });
  };

  public exportMainFilter = (): void => {
    const mainFilter: any = this.getFilterState();

    this.context?.updateParent({
      mainFilter,
    });

    window.store.dispatch(setFilterData(mainFilter));
  };

  public notifyFilterChange = (): void => {
    this.context?.updateParent({
      onFilterChange: this.getFilterState(),
    });
  };

  public getFilterState = (): any => {
    const { state } = this;
    /* clean the filter */
    return _.cloneDeep(state);
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public render(): JSX.Element {
    const { id, sensorArray } = this.state;

    const sensors: DataSelect2[] = sensorArray
      .filter((item: SensorDO) => item.latitude != null)
      .map((item: SensorDO): DataSelect2 => {
        return {
          title: `${item.code} - ${item.name}`,
          text: `${item.description}`,
          id: item.id,
          'data-class': 'select2-results__option2'
        };
      });

    // Default option all sensors
    sensors.unshift({ text: 'ALL SENSORS', id: 0 });

    return (
      <div className="main-filters">
        <UiValidate>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              value={id}
              onChange={this.handleChange}
              data-ctype="number"
              data={sensors}
              options={{ placeholder: 'ALL SENSORS' }}
              style={{ width: '100%' }}
              defaultValue={0}
            />
          </div>
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.submit}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
